/**
 * This composable returns a job post page object for the job post page
 * The data is fetched from an external API (an OLD TGTG one which pulls from Greenhouse - our jobs board provider)
 *
 */

import { OBJECT, DOCUMENT } from '@pkg/config/sanity/schemas';
import { THEME } from '@pkg/config/misc/theme';
import type { PageSeo } from '@pkg/sanity-utils/types/seo';
import type { BagsApiResponse, SurpriseBag } from '@pkg/types/surpriseBag';
import { SURPRISE_BAG_NEARBY_BAGS_FETCH_SIZE } from '@pkg/types/surpriseBag';
import { defaultLocale } from '@pkg/config/languages';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { logger } from '@pkg/logger';
import { getPrice } from '@pkg/utilities/surprisebag';
import { getLanguageCodeFromLocale } from '@pkg/utilities/i18n';
import type { SurpriseBagByIdResponse } from '~/app/types/brandProfiles';
import { selectNearbyBags, getSurpriseBagCanonicalPath } from '~/app/helpers/surpriseBagHelper';

import { metaLabels } from '#build/sanity--content/meta-labels';

const substituteTemplateVars = (template: string, variables: Record<string, string | number>): string => {
  if (!template) { return ''; }
  return template.replace(/\$\{(\w+)\}/g, (match, varName) => {
    return variables[varName]?.toString() ?? match;
  });
};

const getMetaLabels = (locale: string): any => {
  const labels = (metaLabels as Record<string, any>)[locale];
  if (labels) {
    logger.info('✔ Meta labels found for locale:', locale);
    return labels;
  }
  logger.warn('⚠ No meta labels found for locale:', locale);
  return {};
};

/** Aligns with server `getApiBase` — only digit strings are valid bag item ids. */
const isValidSurpriseBagId = (id: string | null | undefined): id is string =>
  id != null && /^\d+$/.test(id);

/**
 * Nearby bags around the store, from the existing bag search API by coordinates.
 * One request for the nearest bags regardless of stock; the helper then picks the
 * available ones first and fills up with unavailable ones.
 */
async function fetchNearbyBags(bag: SurpriseBag, locale: string): Promise<SurpriseBag[]> {
  const coordinates = bag.pickupLocation?.location ?? (bag.store?.storeLocation?.location as { latitude?: number; longitude?: number } | undefined);
  const latitude = coordinates?.latitude;
  const longitude = coordinates?.longitude;
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    return [];
  }

  const { data } = await useFetch<BagsApiResponse>('/api/surprise-bags', {
    query: {
      lat: latitude,
      lng: longitude,
      language: getLanguageCodeFromLocale(locale as LOCALE_CODE),
      locale,
      page: 0,
      size: SURPRISE_BAG_NEARBY_BAGS_FETCH_SIZE,
    },
  });

  return selectNearbyBags(data.value?.payload?.items, bag.item?.itemId);
}

export async function useSurpriseBagData(
  params?: Record<string, any>,
) {
  const locale = params?.locale || defaultLocale;
  const extractedId:string | null = params?.rawId?.includes('-') ? params?.rawId?.split('-').pop() || null : params?.rawId || null;

  const metaLabelsData = getMetaLabels(locale);
  const fetchResult = isValidSurpriseBagId(extractedId)
    ? await useFetch<SurpriseBagByIdResponse>(`/api/surprise-bags/${extractedId}`, { query: { locale } })
    : { data: ref(null) };
  const { data: response } = fetchResult;

  const responseValue = response.value;
  const bagPayload: SurpriseBag | undefined = responseValue?.payload || undefined;

  const bagData = bagPayload;
  let locationFinderData;
  const brandProfileLink = responseValue?.brandProfileLink ?? null;

  if (bagData === undefined) {
    // Fetch location finder data via API route (server-side only has access to Sanity token)
    const { data: locationFinderResponse } = await useFetch('/api/sanity/location-finder', {
      query: { locale },
    });
    locationFinderData = locationFinderResponse.value;
  }

  // Nearby bags around this store. Fetched here, with the page data, so the
  // section is part of the server-rendered HTML and its links are crawlable.
  const nearbyBags = bagData ? await fetchNearbyBags(bagData, locale) : [];

  const payload = reactive([
    {
      _type: OBJECT.MODULAR_OUTER_BLOCK_SURPRISE_BAG,
      content: {
        bag: bagData,
        brandProfileLink,
        nearbyBags,
      },
    },
  ]);

  const surpriseBag = {
    _type: OBJECT.MODULAR_OUTER_BLOCK_SECTION,
    theme: THEME.DARK,
    variant: 'noImage',
    content: payload,
  };

  const metaVariables = {
    storeName: bagData?.store?.storeName || 'Store Name',
    branch: bagData?.store?.branch || '',
    city: bagData?.store?.storeLocation?.address?.city || 'City',
    price: getPrice(bagData?.item?.itemPrice, locale),
  };

  const metaTitle = substituteTemplateVars(metaLabelsData?.metaTitle, metaVariables);
  const metaDescription = substituteTemplateVars(metaLabelsData?.metaDescription, metaVariables);

  // A bag resolves by its id alone, so it can be viewed under any locale or slug. Always canonical
  // to the one URL built from the bag's own data (home locale + sitemap-style slug).
  const canonicalPath = getSurpriseBagCanonicalPath(bagData);
  const canonicalUrl = canonicalPath ? `${useRuntimeConfig().public.domain}${canonicalPath}` : undefined;

  const pageContent = bagData
    ? {
        canonicalUrl,
        seoSocial: <PageSeo>{
          metaTitle: metaTitle || `${metaVariables.storeName} - | Too Good To Go in ${metaVariables.city} - Save good food near you`,
          ogTitle: metaTitle || `${metaVariables.storeName} - | Too Good To Go in ${metaVariables.city} - Save good food near you`,
          metaDescription: metaDescription || 'Too Good To Go',
          ogDescription: metaDescription || 'Too Good To Go',
        },
        _type: DOCUMENT.PAGES,
        content: [
          surpriseBag,
        ],
      }
    : {
        content: [{
          ...locationFinderData[0],
          is404: true,
        }],
      };
  try {
    return {
      data: ref({
        page: pageContent,
      }),
    };
  } catch (error) {
    logger.info('[Find surprise bag async] ', error);
    return null;
  }
};
